import { FEE_CALC_FALLBACK_VOLUME, FEE_CALC_INPUT_CAP, FEE_CALC_STOPS } from './constants';

/**
 * Fee calculator math (Asana 1219949663037427, Decision 6; range per Asana
 * 1219988885932257, Decision 3). Pure: every input comes from the live fee config
 * (MONTHLY_MAINTENANCE_FEE_POINTS, the admin rate and the Elite discount) and the
 * slider or the typed value. Integers, rounded half-up.
 */

/** Percent to fraction; the one 100 in this folder besides the waived check. */
const PERCENT = 100;

export interface FeeSavingsInput {
  /** Points traded in the month. */
  volume: number;
  /** MONTHLY_MAINTENANCE_FEE_POINTS. */
  maintenanceFee: number;
  /** The transaction fee rate, e.g. 2 for 2%. */
  ratePct: number;
  /** The Elite discount off both fees, e.g. 100 for waived. */
  eliteDiscountPct: number;
}

export interface FeeSavings {
  freeMaintenance: number;
  freeTransaction: number;
  freeMonthly: number;
  eliteMaintenance: number;
  eliteTransaction: number;
  eliteMonthly: number;
  yearlySavings: number;
  /** True when the Elite discount is the full fee. */
  waived: boolean;
}

const discounted = (amount: number, eliteDiscountPct: number): number =>
  Math.round((amount * (PERCENT - eliteDiscountPct)) / PERCENT);

export const computeFeeSavings = ({ volume, maintenanceFee, ratePct, eliteDiscountPct }: FeeSavingsInput): FeeSavings => {
  const freeMaintenance = Math.round(maintenanceFee);
  const freeTransaction = Math.round((volume * ratePct) / PERCENT);
  const freeMonthly = freeMaintenance + freeTransaction;
  const waived = eliteDiscountPct >= PERCENT;
  const eliteMaintenance = waived ? 0 : discounted(freeMaintenance, eliteDiscountPct);
  const eliteTransaction = waived ? 0 : discounted(freeTransaction, eliteDiscountPct);
  const eliteMonthly = eliteMaintenance + eliteTransaction;
  return {
    freeMaintenance,
    freeTransaction,
    freeMonthly,
    eliteMaintenance,
    eliteTransaction,
    eliteMonthly,
    yearlySavings: (freeMonthly - eliteMonthly) * 12,
    waived,
  };
};

/**
 * Where the calculator starts: the signed-in trader's 90-day average exactly as
 * the backend returns it (never rounded or clamped), otherwise the example month.
 */
export const defaultSliderVolume = (avgMonthlyTradedPoints90d: number | null | undefined): number =>
  avgMonthlyTradedPoints90d == null ? FEE_CALC_FALLBACK_VOLUME : avgMonthlyTradedPoints90d;

/**
 * The slider's stops for a given starting value: the progressive FEE_CALC_STOPS,
 * extended by the trader's own number when it sits above the last stop so the
 * range stretches to fit.
 */
export const stopsFor = (startVolume: number | null | undefined): number[] => {
  const last = FEE_CALC_STOPS[FEE_CALC_STOPS.length - 1];
  return startVolume != null && startVolume > last ? [...FEE_CALC_STOPS, startVolume] : [...FEE_CALC_STOPS];
};

/** Index of the stop closest to a value; on a tie the lower stop wins. */
export const nearestStopIndex = (stops: readonly number[], value: number): number => {
  let best = 0;
  for (let i = 1; i < stops.length; i += 1) {
    if (Math.abs(stops[i] - value) < Math.abs(stops[best] - value)) best = i;
  }
  return best;
};

/** A typed value: digits only, whole points, capped. */
export const clampFeeInput = (value: number): number =>
  Math.min(FEE_CALC_INPUT_CAP, Math.max(0, Math.floor(Number.isFinite(value) ? value : 0)));
